import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { sanitizeInvertedYAxisRanges } from 'features/timeSeries/utils/chartSerialization'
import useSWR, { SWRConfiguration } from 'swr'
import { StandaloneChart, StandaloneChartSchema } from 'types'

/**
 * Overlay the tool-state fields the resolve service does not pass through.
 *
 * Ruler/annotation state is saved straight to the rolodex `chart` entry, but
 * `/api/v1/chart/{id}/resolved` reconstructs a curated subset that omits
 * `ruler_configs` / `point_annotation_configs` / `interaction_mode`. So they
 * round-tripped invisibly: written to rolodex, dropped on the way back, and
 * (because the schema fields are nullish) silently read as empty — a chart that
 * "saved" its rulers but never showed them. Until the resolve service carries
 * them, read them from the rolodex entry and overlay onto the resolved chart.
 */
export const overlayToolState = (
  resolved: StandaloneChart,
  rolodexEntryData: unknown
): StandaloneChart => {
  const parsed = StandaloneChartSchema.safeParse(
    sanitizeInvertedYAxisRanges(rolodexEntryData)
  )
  if (!parsed.success) return resolved
  return {
    ...resolved,
    rulerConfigs: parsed.data.rulerConfigs ?? resolved.rulerConfigs,
    pointAnnotationConfigs:
      parsed.data.pointAnnotationConfigs ?? resolved.pointAnnotationConfigs,
    interactionMode: parsed.data.interactionMode ?? resolved.interactionMode,
  }
}

export const useResolvedChart = (
  chartId: string | undefined,
  config?: SWRConfiguration<StandaloneChart, Error>
) => {
  const { authorization, authorizedAxios } = useGeckoAuth()
  const { TIME_SERIES_SERVICE, ROLODEX_URL } = useRuntimeVariables()

  const fetcher = async (id: string): Promise<StandaloneChart> => {
    const [resolvedResponse, rolodexEntryData] = await Promise.all([
      authorizedAxios.get(`${TIME_SERIES_SERVICE}/api/v1/chart/${id}/resolved`),
      // Best-effort: the chart still resolves if this read fails; the user just
      // loses the tool-state overlay, never the chart itself.
      authorizedAxios
        .post(`${ROLODEX_URL}/api/v2/entries/load`, {
          type: ['chart'],
          ids: [id],
          limit: 1,
        })
        .then((response) => {
          const wrappers = response.data as
            | Array<{ entry?: { data?: unknown } }>
            | undefined
          return wrappers?.[0]?.entry?.data ?? null
        })
        .catch(() => null),
    ])

    // Sanitize before the throwing parse: an inverted y-axis range (min > max)
    // saved by a build that did not validate at commit time must cost only
    // that bound — not make the chart unloadable on every subsequent visit.
    const resolved = StandaloneChartSchema.parse(
      sanitizeInvertedYAxisRanges(resolvedResponse.data)
    )
    return rolodexEntryData
      ? overlayToolState(resolved, rolodexEntryData)
      : resolved
  }

  const { data, ...rest } = useSWR<StandaloneChart, Error>(
    chartId && authorization ? ['resolved-chart', chartId] : null,
    ([, id]: [string, string]) => fetcher(id),
    config
  )

  return {
    chart: data,
    ...rest,
  }
}
